/** Percent-per-ad-unit editor with a live "sums to 100" indicator. */

import React, { useId } from 'react';
import type { MediaPlanUnitDef, MediaPlanUnitKey } from './types';
import { mixTotal } from './planDraft';
import { labelStyle, numberFieldStyle } from './formStyles';

interface Props {
  units: MediaPlanUnitDef[];
  mix: Partial<Record<MediaPlanUnitKey, string>>;
  onChange: (next: Partial<Record<MediaPlanUnitKey, string>>) => void;
  disabled?: boolean;
  compact?: boolean;
}

const UnitMixEditor: React.FC<Props> = ({ units, mix, onChange, disabled, compact }) => {
  const idPrefix = useId();
  const total = Math.round(mixTotal(mix) * 100) / 100;
  const ok = Math.abs(total - 100) < 0.01;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(auto-fit, minmax(120px, 1fr))' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {units.map((u) => (
          <div key={u.key}>
            <label htmlFor={`${idPrefix}-${u.key}`} style={{ ...labelStyle, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
              {u.shortLabel}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id={`${idPrefix}-${u.key}`}
                type="number"
                min={0}
                max={100}
                step={5}
                inputMode="decimal"
                style={{ ...numberFieldStyle, paddingRight: 26 }}
                value={mix[u.key] ?? ''}
                placeholder="0"
                disabled={disabled}
                aria-label={`${u.label} share of budget, percent`}
                onChange={(e) => onChange({ ...mix, [u.key]: e.target.value })}
              />
              <span style={styles.pct}>%</span>
            </div>
          </div>
        ))}
      </div>
      <div role="status" style={{ ...styles.total, color: ok ? 'var(--success)' : 'var(--danger)' }}>
        Total {total}% {ok ? '✓' : '— must sum to 100'}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pct: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--fg-4)', pointerEvents: 'none' },
  total: { marginTop: 8, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
};

export default UnitMixEditor;
